import { useState } from "react";
import type { FormEvent } from "react";

const FORM_ID = "about-feedback-form";
const FORM_SUBMIT_ADDR = "https://readdy.ai/api/form/dbb69aectmbk4sp5cc6g";

const FEEDBACK_TYPES = ["内容纠错", "剧集缺失", "播放问题", "合作与建议", "其他"];

export default function FeedbackForm() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("contact_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }

    formData.delete("contact_alt");
    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      payload.append(key, typeof value === "string" ? value : value.name);
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(FORM_SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        data?: unknown;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMessage =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = `${serverMessage || ""}`.toLowerCase().includes("spam");
      const isSuccess = response.ok && parsed?.code === "OK" && !isSpam;

      if (isSuccess) {
        setStatus("success");
        form.reset();
        return;
      }

      setStatus("error");
      setFormError(
        serverMessage && typeof serverMessage === "string" && serverMessage.length < 200
          ? serverMessage
          : "提交失败，请稍后重试，或检查填写内容是否完整。",
      );
    } catch {
      setStatus("error");
      setFormError("网络异常，提交没有成功，请检查网络后重试。");
    }
  };

  return (
    <div id="feedback" className="rounded-lg border border-background-200 bg-background-50">
      <div className="border-b border-background-200 px-5 py-4">
        <h2 className="font-heading text-lg font-bold text-foreground-950">意见反馈</h2>
        <p className="mt-1 text-xs text-foreground-500">
          发现剧集信息有误、资料缺失，或想推荐一部好剧？留言告诉我们。
        </p>
      </div>

      <form
        id={FORM_ID}
        data-readdy-form
        onSubmit={handleSubmit}
        className="box-border px-5 py-5"
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="min-w-0">
            <label htmlFor="feedback-name" className="mb-1.5 block text-xs font-medium text-foreground-700">
              称呼
            </label>
            <input
              id="feedback-name"
              name="name"
              type="text"
              required
              maxLength={40}
              placeholder="怎么称呼你"
              className="box-border w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
            />
          </div>
          <div className="min-w-0">
            <label htmlFor="feedback-email" className="mb-1.5 block text-xs font-medium text-foreground-700">
              邮箱
            </label>
            <input
              id="feedback-email"
              name="email"
              type="email"
              required
              placeholder="用于接收回复"
              className="box-border w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
            />
          </div>
          <div className="min-w-0 sm:col-span-2">
            <label htmlFor="feedback-type" className="mb-1.5 block text-xs font-medium text-foreground-700">
              反馈类型
            </label>
            <select
              id="feedback-type"
              name="type"
              required
              defaultValue=""
              className="box-border w-full cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400"
            >
              <option value="" disabled>
                请选择反馈类型
              </option>
              {FEEDBACK_TYPES.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>
          <div className="min-w-0 sm:col-span-2">
            <label htmlFor="feedback-message" className="mb-1.5 block text-xs font-medium text-foreground-700">
              反馈内容
            </label>
            <textarea
              id="feedback-message"
              name="message"
              required
              maxLength={500}
              rows={5}
              placeholder="请描述你遇到的问题或建议（500 字以内）"
              className="box-border w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
            />
          </div>
        </div>

        <div className="form-note-alt" aria-hidden="true">
          <label htmlFor="feedback-contact-alt">请勿填写此项</label>
          <input
            id="feedback-contact-alt"
            name="contact_alt"
            type="text"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            readOnly
          />
        </div>

        {status === "success" && (
          <p className="mt-4 flex items-start gap-1.5 rounded-md bg-secondary-100 px-3 py-2.5 text-xs leading-relaxed text-secondary-900">
            <i className="ri-checkbox-circle-line mt-0.5 text-sm" aria-hidden="true" />
            反馈已提交，感谢你的建议！我们会尽快查看并处理。
          </p>
        )}
        {status === "error" && (
          <p className="mt-4 flex items-start gap-1.5 rounded-md bg-accent-100 px-3 py-2.5 text-xs leading-relaxed text-accent-900">
            <i className="ri-error-warning-line mt-0.5 text-sm" aria-hidden="true" />
            {formError}
          </p>
        )}

        <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-[11px] leading-relaxed text-foreground-500">
            提交即表示你同意我们仅将信息用于反馈处理，不会用于其他用途。
          </p>
          <button
            type="submit"
            disabled={status === "submitting"}
            className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {status === "submitting" ? (
              <>
                <i className="ri-loader-4-line animate-spin" aria-hidden="true" />
                提交中
              </>
            ) : (
              <>
                <i className="ri-send-plane-line" aria-hidden="true" />
                提交反馈
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}